import { useMemo } from 'react'

import {
  type UseQueryResult,
  useQueries,
  useQuery,
} from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { pursuitSchemasQueryKeys } from '@/hooks/pursuits/schemas/types'
import { normalizeOrgIds } from '@/hooks/pursuits/types'

import type { PaginatedResponse } from '@/types/Api'
import type { ExtendedJsonSchema } from '@/types/extended-schema'
import type { UUID } from '@/types/Id'

interface CommonArgs<T> {
  /** Selector applied to the merged, flat ExtendedJsonSchema[]. */
  select?: (schemas: ExtendedJsonSchema[]) => T
}

interface SingleOrgArgs<T> extends CommonArgs<T> {
  orgId: UUID | ''
  orgIds?: never
}

interface MultiOrgArgs<T> extends CommonArgs<T> {
  orgIds: UUID[]
  orgId?: never
}

type UsePursuitSchemasArgs<T> = SingleOrgArgs<T> | MultiOrgArgs<T>

interface UsePursuitSchemasResult<T> {
  data: T | undefined
  isLoading: boolean
  isError: boolean
}

const combinePursuitSchemas = (
  results: UseQueryResult<PaginatedResponse<ExtendedJsonSchema>>[],
) => {
  const flat: ExtendedJsonSchema[] = []
  for (const r of results) {
    if (r.data) flat.push(...r.data.data)
  }
  return {
    flat,
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    allUndefined: results.every((r) => r.data === undefined),
  }
}

const fetchPursuitSchemas = async (
  orgId: UUID | '',
  accessToken: string,
): Promise<PaginatedResponse<ExtendedJsonSchema>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/pursuit-schemas`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
  return (await res.json()) as PaginatedResponse<ExtendedJsonSchema>
}

export const usePursuitSchemas = <T = ExtendedJsonSchema[]>(
  args: UsePursuitSchemasArgs<T>,
): UsePursuitSchemasResult<T> => {
  const { accessToken } = useAuthInfo()
  const { select } = args
  const orgIds = normalizeOrgIds(args)
  const isSingle = orgIds.length <= 1
  const singleOrgId = orgIds[0] ?? ''

  const singleQuery = useQuery({
    queryKey: pursuitSchemasQueryKeys.organization({ orgId: singleOrgId }),
    queryFn: () => {
      if (!accessToken) throw new Error('No access token provided')
      return fetchPursuitSchemas(singleOrgId, accessToken)
    },
    enabled: isSingle && Boolean(singleOrgId) && Boolean(accessToken),
  })

  const multi = useQueries({
    queries: isSingle
      ? []
      : orgIds.map((orgId) => ({
          queryKey: pursuitSchemasQueryKeys.organization({ orgId }),
          queryFn: () => {
            if (!accessToken) throw new Error('No access token provided')
            return fetchPursuitSchemas(orgId, accessToken)
          },
          enabled: Boolean(orgId) && Boolean(accessToken),
        })),
    combine: combinePursuitSchemas,
  })

  const data = useMemo<T | undefined>(() => {
    if (isSingle) {
      if (singleQuery.data === undefined) return undefined
      const flat = singleQuery.data.data
      return select ? select(flat) : (flat as unknown as T)
    }
    if (multi.allUndefined) return undefined
    return select ? select(multi.flat) : (multi.flat as unknown as T)
  }, [isSingle, singleQuery.data, multi.allUndefined, multi.flat, select])

  if (isSingle) {
    return {
      data,
      isLoading: singleQuery.isLoading,
      isError: singleQuery.isError,
    }
  }

  return {
    data,
    isLoading: multi.isLoading,
    isError: multi.isError,
  }
}

interface UseLatestPursuitSchemaArgs {
  orgId?: UUID | ''
  orgIds?: UUID[]
}

export const selectLatestSchema = (
  schemas: ExtendedJsonSchema[],
): ExtendedJsonSchema | undefined => {
  const active = schemas.filter((schema) => !schema.archived)
  if (active.length === 0) return undefined
  return active.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )[0]
}

export const useLatestPursuitSchema = (args: UseLatestPursuitSchemaArgs) => {
  return usePursuitSchemas({
    ...(args.orgIds ? { orgIds: args.orgIds } : { orgId: args.orgId ?? '' }),
    select: selectLatestSchema,
  } as UsePursuitSchemasArgs<ExtendedJsonSchema | undefined>)
}

const selectLatestSchemaId = (
  schemas: ExtendedJsonSchema[],
): UUID | undefined => {
  return selectLatestSchema(schemas)?.id
}

interface UseLatestPursuitSchemaIdArgs {
  orgId?: UUID | ''
  orgIds?: UUID[]
}

export const useLatestPursuitSchemaId = (
  args: UseLatestPursuitSchemaIdArgs,
) => {
  return usePursuitSchemas({
    ...(args.orgIds ? { orgIds: args.orgIds } : { orgId: args.orgId ?? '' }),
    select: selectLatestSchemaId,
  } as UsePursuitSchemasArgs<UUID | undefined>)
}

const selectHasAtleastOneSchema = (schemas: ExtendedJsonSchema[]): boolean => {
  return schemas.some((schema) => !schema.archived)
}

interface UseOrganizationHasAtleastOnePursuitSchemaArgs {
  orgId?: UUID | ''
  orgIds?: UUID[]
}

export const useOrganizationHasAtleastOnePursuitSchema = (
  args: UseOrganizationHasAtleastOnePursuitSchemaArgs,
) => {
  return usePursuitSchemas({
    ...(args.orgIds ? { orgIds: args.orgIds } : { orgId: args.orgId ?? '' }),
    select: selectHasAtleastOneSchema,
  } as UsePursuitSchemasArgs<boolean>)
}
